import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  BrandRatingPending,
  getBusinessPendingRatings,
  getCreatorPendingRatings,
  submitBusinessRating,
  submitCreatorRating,
} from '../../api/payment/brand-deal.api';
import { dealStatusLabel } from '../../utils/brand-deal';
import { isNeedToRateReason, ratingDueLabel } from '../../utils/brand-rating';
import BrandRatingForm from './brand-rating-form';
import { IndividualOrdererBadge } from './orderer-badge';

/**
 * Tab "Chờ chấm điểm" (M5, Task 3) — danh sách deal đã xong còn chờ chấm điểm + ô chấm ngay, dùng
 * chung creator (`brand-deals.page.tsx?tab=ratings`) và doanh nghiệp (mục "Hợp tác creator" trang
 * Quảng cáo). Mirror `brand-market-applications.tsx` (forwardRef reload) — cha cần gọi lại sau khi
 * danh sách đổi (vd mở trang vừa lúc có deal mới xong) và cần biết số lượng cho dải nhắc đầu trang.
 */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

/** Lỗi "Bạn cần chấm điểm các deal đã xong trước khi làm việc mới." (global-constraints) — nếu có
 * `onGoToRatingsTab`, gắn thêm nút "Chấm điểm ngay" ngay trên toast để chuyển sang tab này. Bật ra từ lỗi
 * khi bấm (nhận lời mời, xin tham gia sàn, nhận brief, doanh nghiệp mời, mở sàn, duyệt đơn, tạo hoặc gửi
 * duyệt chiến dịch). */
export const showNeedToRateToast = (message: string, onGoToRatingsTab?: () => void) => {
  if (onGoToRatingsTab && isNeedToRateReason(message)) {
    toast.error((t) => (
      <span className="flex flex-wrap items-center gap-2">
        <span>{message}</span>
        <button
          type="button"
          onClick={() => {
            toast.dismiss(t.id);
            onGoToRatingsTab();
          }}
          className="shrink-0 rounded-full border border-[#0057FF] px-2.5 py-1 text-[12px] font-semibold text-[#0057FF]"
        >
          Đánh giá ngay
        </button>
      </span>
    ));
    return;
  }
  toast.error(message);
};

export interface BrandRatingPendingListHandle {
  reload: () => void;
}

interface BrandRatingPendingListProps {
  viewerRole: 'Creator' | 'Business';
  /** Báo cho cha số deal còn chờ chấm (dải nhắc "Bạn có N deal cần chấm điểm" đầu trang). */
  onCountChange?: (count: number) => void;
}

const BrandRatingPendingList = React.forwardRef<BrandRatingPendingListHandle, BrandRatingPendingListProps>(
  ({ viewerRole, onCountChange }, ref) => {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState<BrandRatingPending[]>([]);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [nowMs, setNowMs] = useState(() => Date.now());

    const load = useCallback(() => {
      setLoading(true);
      const req = viewerRole === 'Creator' ? getCreatorPendingRatings() : getBusinessPendingRatings();
      req
        .then((res) => {
          setItems(res);
          onCountChange?.(res.length);
        })
        .catch(() => toast.error('Không tải được danh sách chờ đánh giá.'))
        .finally(() => setLoading(false));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewerRole]);

    useEffect(() => {
      load();
    }, [load]);

    useImperativeHandle(ref, () => ({ reload: load }), [load]);

    // Cập nhật đồng hồ đếm hạn chấm điểm mỗi phút — không cần chính xác tới giây.
    useEffect(() => {
      const t = setInterval(() => setNowMs(Date.now()), 60000);
      return () => clearInterval(t);
    }, []);

    const handleSubmit = async (dealId: string, stars: number, comment: string) => {
      setBusyId(dealId);
      try {
        const submit = viewerRole === 'Creator' ? submitCreatorRating : submitBusinessRating;
        await submit(dealId, stars, comment || undefined);
        toast.success('Đã gửi đánh giá.');
        setItems((prev) => {
          const next = prev.filter((x) => x.dealId !== dealId);
          onCountChange?.(next.length);
          return next;
        });
      } catch (err) {
        toast.error(errorMessage(err, 'Không gửi được đánh giá.'));
      } finally {
        setBusyId(null);
      }
    };

    if (loading) {
      return (
        <div className="flex justify-center p-8">
          <Loader2 className="animate-spin ui-muted" />
        </div>
      );
    }

    if (items.length === 0) {
      return <p className="ui-muted p-4 text-center text-[14px]">Không có deal nào chờ đánh giá.</p>;
    }

    return (
      <div className="flex flex-col gap-3">
        {items.map((item) => (
          <div key={item.dealId} className="ui-card flex flex-col gap-2 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="ui-heading text-[15px] font-bold">{item.campaignTitle}</div>
              <span className="ui-muted text-[13px]">{dealStatusLabel(item.outcome)}</span>
            </div>
            <div className="ui-muted text-[13px]">
              {item.counterpartName} <IndividualOrdererBadge kind={item.ordererKind} /> · {ratingDueLabel(item.dueAt, nowMs)}
            </div>
            <BrandRatingForm busy={busyId === item.dealId} onSubmit={(stars, comment) => handleSubmit(item.dealId, stars, comment)} />
          </div>
        ))}
      </div>
    );
  }
);

BrandRatingPendingList.displayName = 'BrandRatingPendingList';

export default BrandRatingPendingList;
